<template>
  <b-modal ref="modal"
           :modal-class="`artisio-modal ${modalClass} `"
           :visible="visible"
           :size="size"
           :title="title"
           :hide-footer="hideFooter"
           no-enforce-focus
           :scrollable="scrollable"
           :body-class="bodyClass"
           @show="onModalShow"
           @shown="$emit('shown')"
           @hide="beforeHide"
           :no-close-on-backdrop="noCloseOnBackdrop"
           :no-close-on-esc="noCloseOnEsc"
           :hide-header-close="hideHeaderClose"
           @hidden="onHidden">
    <template v-if="!$slots['modal-footer']" v-slot:modal-footer>
      <cancel-button @click="hide" :disabled="isLoading"></cancel-button>
      <submit-button
          v-if="additionalOkTitle"
          @click="onSaveAdditionalClick"
          :disabled="disableSubmitBtn || isLoading"
          :text="additionalOkTitle"
          :show-loader="isLoading"/>
      <b-tooltip v-if="submitHint" :target="`modal-submit-${_uid}`" :title="submitHint"></b-tooltip>
      <submit-button @click="onSaveClick" :disabled="btnDisabled || isLoading" :text="okTitle"
                     :id="`modal-submit-${_uid}`" :show-loader="isLoading"></submit-button>
    </template>
    <b-form v-if="contentInForm" @submit.prevent="onSaveClick">
      <!--      Output default slot content-->
      <slot></slot>
      <!--      Output names slots-->
      <slot v-for="(_, name) in $slots" :name="name" :slot="name"/>
      <button style="display: none"></button>
    </b-form>
    <template v-else>
      <slot v-for="(_, name) in $slots" :name="name" :slot="name"/>
    </template>
    <div v-if="modalLoading" class="modal-spinner text-info">
      <b-spinner/>
    </div>
  </b-modal>
</template>

<script>
  /**
   * @author Saiat Kalbiev <kalbievich11@gmail.com>
   */
  import i18n from './../../../shared/i18n';
  import CancelButton from "../common/CancelButton";
  import SubmitButton from "../common/SubmitButton";
  import {Utils} from '@/core/services/utils.service'

  export default {
    name: 'ArtisioModal',
    components: {SubmitButton, CancelButton},
    data() {
      return {
        forceClose: false,
        oldState: ''
      };
    },
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      hideFooter: {
        type: Boolean,
        default: false
      },
      contentInForm: {
        type: Boolean,
        default: true
      },
      data: {
        type: Object,
        default: null
      },
      size: {
        type: String,
        default: 'md'
      },
      title: {
        type: String,
        default: ''
      },
      scrollable: {
        type: Boolean,
        default: false
      },
      showConfirmation: {
        type: Boolean,
        default: false
      },
      confirmationTitle: {
        type: String,
        default: null
      },
      confirmationMessage: {
        type: String,
        default: i18n.t('Are you sure you want to discard current changes?')
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      bodyClass: {
        type: [String, Object, Array],
        default: 'pb-0'
      },
      disableSubmitBtn: {
        type: Boolean,
        default: false
      },
      okTitle: {
        type: String,
        default: i18n.t('Submit')
      },
      additionalOkTitle: {
        type: String,
      },
      modalClass: {
        type: String,
        default: ''
      },
      modalLoading: {
        type: Boolean,
        default: false,
      },
      noCloseOnBackdrop: {
        type: Boolean,
        default: false
      },
      noCloseOnEsc: {
        type: Boolean,
        default: false
      },
      hideHeaderClose: {
        type: Boolean,
        default: false
      },
      submitHint: {
        type: String,
        default: null
      }
    },
    watch: {
      // Watch data prop and if it is changed save it inside oldState
      data() {
        this.oldState = this.getState();
      }
    },
    methods: {
      onModalShow() {
        this.forceClose = false;
        this.$emit('show', '');
      },
      beforeHide($event) {
        this.$emit('hide', $event);
        if (!this.dataChanged() || !this.showConfirmation) {
          return;
        }
        if (!this.forceClose) {
          $event.preventDefault();
          this.$confirm(this.confirmationMessage)
            .then(async (success) => {
              if (success) {
                this.forceClose = true;
                this.hide()
              }
            });
        }
      },
      dataChanged() {
        return Utils.isDifferent(JSON.parse(this.getState()), JSON.parse(this.oldState));
      },
      onHidden() {
        this.$emit('hidden');
      },
      hide() {
        this.$refs.modal.hide();
      },
      async onSaveClick($event) {
        this.$emit('ok', $event);
      },
      async onSaveAdditionalClick($event) {
        this.$emit('additionalOk', $event);
      },
      getState() {
        if (!this.data) {
          return false;
        }
        const myData = {...this.data};
        delete myData.rules;
        delete myData.errors;
        delete myData.attributeLabels;
        return JSON.stringify(myData);
      },
      updateOldState() {
        this.oldState = this.getState();
      }
    },
    computed: {
      btnDisabled() {
        if (this.disableSubmitBtn) {
          return true
        }
        return this.isLoading;
      }
    },
    mounted() {
      this.updateOldState();
    }
  };
</script>

<style lang="scss" scoped>
  .modal-spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 9999;
    background-color: white;
    opacity: 0.8;

    > span {
      position: absolute;
      top: 50%;
      left: 48%;
      width: 3rem;
      height: 3rem;
    }
  }
</style>
